import React from 'react';

import { socialCardKeys } from '@/src/constants/socialcard.constant';
import ClassNames from '@/src/components/componentHelper/ClassNames';

/**
 * Shared media parts for the studio card set (widget types 10-19).
 *
 * DELIBERATELY FREE OF hls.js. The video player lives in StudioVideo.jsx and
 * is loaded dynamically, so a customer on an older widget type never
 * downloads the HLS runtime. Importing the hook here would undo that.
 *
 * Ported from famewall-React/src/components/cards/studio-cards/StudioMedia.
 * Same rules, same look; the differences are environmental - the theme is a
 * `t` prop rather than a context hook, and video runs on the embed's
 * useLazyHlsPlayback hook so a wall of videos doesn't fetch every manifest up
 * front.
 *
 * Video and audio use the SAME format GraphicVideoCard uses: the player
 * carries the play control and the person's name and work title over a bottom
 * gradient, and that is the WHOLE card. No header, no meta rail, no border,
 * no padding around it.
 *
 * The ONE exception is the summary. When a testimonial has one, the player
 * extends downward into an enclosure holding the quote, and THAT enclosure is
 * styled like the host widget so the pair reads as one object. With no summary
 * there is no enclosure at all - just the video.
 *
 * Rule for everything in here: NEVER return null for a media testimonial. A
 * missing playback id or asset url must still paint a frame, otherwise a video
 * or audio card silently renders as an empty box and looks like it vanished.
 */

/** The player's own corner when nothing encloses it. */
const MEDIA_RADIUS = 10;

/* ------------------------------------------------------------- media box */

/**
 * The black player surface.
 *
 * Uses the padding-top ratio technique rather than CSS aspect-ratio: it needs
 * no modern-CSS support, and it cannot collapse to zero height inside a flex
 * column or a Muuri item mid-layout.
 */
export const MediaBox = ({ ratio = 4 / 3, radius = MEDIA_RADIUS, children }) => (
  <div
    className="relative w-full"
    style={{
      position: 'relative',
      width: '100%',
      paddingTop: `${(1 / ratio) * 100}%`,
      borderRadius: radius,
      overflow: 'hidden',
      backgroundColor: '#000',
    }}
  >
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>{children}</div>
  </div>
);

/* --------------------------------------------------------------- overlay */

/**
 * Play control on the left, the person on the right - the old cards' bar.
 *
 * `isPlaying` swaps the glyph to a pause bar. Audio keeps this overlay mounted
 * the whole time and relies on it to stop playback (its canvas takes no
 * clicks); video hides it while playing because the <video> itself is the
 * click target. Never gate BOTH the overlay and the click target on playback,
 * or the testimonial starts with no way to stop it.
 */
export const MediaOverlay = ({ cardItem, onPlay, fontClass, isPlaying = false }) => {
  const link = cardItem && cardItem[socialCardKeys.messageLink];
  const truncate = {
    margin: 0,
    textAlign: 'end',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  };

  return (
    <div
      className="absolute bottom-0 transition ease-in-out duration-300 py-2 px-4 w-full opacity-100 bg-gradient-to-t from-black to-transparent"
      style={{ position: 'absolute', bottom: 0, left: 0, width: '100%' }}
    >
      <div className="flex w-full justify-between items-center">
        <svg
          onClick={onPlay}
          className="h-10 w-10 text-white cursor-pointer"
          viewBox="0 0 32 32"
          fill="none"
          aria-label={isPlaying ? 'Pause' : 'Play'}
          role="button"
          style={{ flex: 'none' }}
        >
          {isPlaying ? (
            <>
              <path
                d="M8.66667 6.66667C8.29848 6.66667 8 6.96514 8 7.33333V24.6667C8 25.0349 8.29848 25.3333 8.66667 25.3333H12.6667C13.0349 25.3333 13.3333 25.0349 13.3333 24.6667V7.33333C13.3333 6.96514 13.0349 6.66667 12.6667 6.66667H8.66667Z"
                fill="currentColor"
              />
              <path
                d="M19.3333 6.66667C18.9651 6.66667 18.6667 6.96514 18.6667 7.33333V24.6667C18.6667 25.0349 18.9651 25.3333 19.3333 25.3333H23.3333C23.7015 25.3333 24 25.0349 24 24.6667V7.33333C24 6.96514 23.7015 6.66667 23.3333 6.66667H19.3333Z"
                fill="currentColor"
              />
            </>
          ) : (
            <path
              d="M10.6667 6.6548C10.6667 6.10764 11.2894 5.79346 11.7295 6.11862L24.377 15.4634C24.7377 15.7298 24.7377 16.2692 24.3771 16.5357L11.7295 25.8813C11.2895 26.2065 10.6667 25.8923 10.6667 25.3451L10.6667 6.6548Z"
              fill="currentColor"
            />
          )}
        </svg>

        <div className={ClassNames('flex flex-col text-white items-end', fontClass)} style={{ minWidth: 0 }}>
          <p className="text-md font-semibold" style={truncate}>
            {cardItem && cardItem[socialCardKeys.authorName]}
          </p>
          <a
            href={link || undefined}
            target={link ? '_blank' : '_self'}
            rel="noreferrer noopener"
            className={ClassNames('text-sm', link ? 'hover:underline customs' : 'no-underline')}
            style={{ ...truncate, color: 'inherit' }}
          >
            {cardItem && cardItem[socialCardKeys.workTitle]}
          </a>
        </div>
      </div>
    </div>
  );
};

export const WaitingSpinner = () => (
  <svg
    version={1.0}
    width="45px"
    height="45px"
    viewBox="0 0 128 128"
    xmlSpace="preserve"
    className="absolute inset-0 m-auto"
  >
    <g>
      <path
        d="M70.1 9.42l-10.7 9.4v-5.77a50.82 50.82 0 0 0-43.7 68.23L11 79.7l-1.5 5.53A58 58 0 0 1 59.4 5.8V0z"
        fill="#F3F4F6"
      />
      <path
        d="M70.1 9.42l-10.7 9.4v-5.77a50.82 50.82 0 0 0-43.7 68.23L11 79.7l-1.5 5.53A58 58 0 0 1 59.4 5.8V0z"
        fill="#F3F4F6"
        transform="rotate(120 64 64)"
      />
      <path
        d="M70.1 9.42l-10.7 9.4v-5.77a50.82 50.82 0 0 0-43.7 68.23L11 79.7l-1.5 5.53A58 58 0 0 1 59.4 5.8V0z"
        fill="#F3F4F6"
        transform="rotate(240 64 64)"
      />
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 64 64"
        to="120 64 64"
        dur="240ms"
        repeatCount="indefinite"
      />
    </g>
  </svg>
);

/* ------------------------------------------------------------- enclosure */

/** True when this testimonial carries a summary to show under the player. */
export const hasSummary = (cardItem) => {
  const s = cardItem && cardItem[socialCardKeys.videoSummary];
  return !!(s && s !== '');
};

/**
 * Wraps the player ONLY when there is a summary to extend into.
 *
 * With a summary: the host widget's own style (border, corner, surface,
 * shadow) becomes an enclosure around player + quote, clipped so the player's
 * top corners take the enclosure's curve and the two read as one object.
 *
 * Without one: nothing. The player is the card, bare.
 */
/**
 * Whether the player gets wrapped.
 *
 * Normally only when there's a summary to extend into. A card sets
 * `always: true` when its frame IS its identity and a bare video would stop
 * looking like that widget at all - Ink Block's ink border and offset shadow,
 * Pinboard's tilt, Prism Edge's gradient edge.
 */
const encloses = (enclosure, cardItem) => !!(enclosure && (hasSummary(cardItem) || enclosure.always));

export const MediaEnclosure = ({ enclosure, cardItem, children }) => {
  if (!enclosure || !encloses(enclosure, cardItem)) return <>{children}</>;
  /* `always` is our flag, not a CSS property - keep it out of the style. */
  const { always, ...frame } = enclosure;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        ...frame,
      }}
    >
      {children}
    </div>
  );
};

/**
 * The player's corner always follows the host card's corner.
 *
 * Enclosed, the enclosure draws it and clips the player (radius 0). Bare, the
 * player draws it itself - taking the SAME value the enclosure would have
 * used, so a square-cornered widget (Ink Block) gets a square video and a
 * soft-cornered one gets a soft video.
 */
export const playerRadius = (enclosure, cardItem) => {
  if (encloses(enclosure, cardItem)) return 0;
  if (enclosure && enclosure.borderRadius != null) return enclosure.borderRadius;
  return MEDIA_RADIUS;
};


/**
 * The transcript Famewall stores for video AND audio testimonials, quoted
 * directly under the player exactly as the older cards quote it.
 *
 * `summaryStyle` is the host card's own body type scale, so a video summary
 * reads in the same type as a text testimonial on that widget. It goes INLINE
 * on the <q>, which is how a card's fontFamily wins over the wall's `font-*`
 * class; the wall font still applies wherever the card sets none. Italic is on
 * the class, so it always survives.
 */
export const MediaSummary = ({ t, cardItem, color, summaryStyle, style, className }) => {
  const summary = cardItem && cardItem[socialCardKeys.videoSummary];
  if (!summary || summary === '') return null;

  return (
    <div
      className="px-3 pt-2 pb-3 text-left"
      style={{
        color: color || t.ink,
        fontSize: t.smallSize ? '1.0em' : '1.15em',
        lineHeight: t.smallSize ? 1.7 : 1.5,
        textDecoration: 'none',
        ...(style || {}),
      }}
    >
      <q
        className={ClassNames('italic', t.fontClass, className || '')}
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...(summaryStyle || {}),
        }}
      >
        {summary}
      </q>
    </div>
  );
};
